import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as AiIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { Node, Edge } from 'reactflow';
import { workflowService } from '../../services/workflow.service';
import { enqueueSnackbar } from 'notistack';

interface AIChange {
  explanation: string;
  add_nodes: any[];
  add_connections: any[];
  remove_nodes: string[];
  update_nodes: any[];
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  changes?: AIChange;
  snapshot?: { nodes: Node[]; edges: Edge[] };
}

interface WorkflowAIChatProps {
  workflowId: string | undefined;
  nodes: Node[];
  edges: Edge[];
  onApplyChanges: (changes: AIChange) => { nodes: Node[]; edges: Edge[] } | void;
  onUndo: (snapshot: { nodes: Node[]; edges: Edge[] }) => void;
  onClose: () => void;
}

export const WorkflowAIChat: React.FC<WorkflowAIChatProps> = ({
  workflowId,
  nodes,
  edges,
  onApplyChanges,
  onUndo,
  onClose,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = async () => {
    const prompt = input.trim();
    if (!prompt || loading) return;

    if (!workflowId) {
      enqueueSnackbar('Please save the workflow first before using AI editing.', { variant: 'warning' });
      return;
    }

    const userMsg: ChatMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: prompt,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const nodePayload = nodes.map((n) => ({
        id: n.id,
        name: n.data?.label || '',
        node_type: n.data?.nodeType || n.type || '',
        parameters: n.data?.parameters || {},
        position_x: Math.round(n.position?.x ?? 0),
        position_y: Math.round(n.position?.y ?? 0),
      }));

      const connPayload = edges.map((e) => ({
        source: e.source,
        target: e.target,
      }));

      const result = await workflowService.aiEdit(workflowId, {
        prompt,
        nodes: nodePayload,
        connections: connPayload,
      });

      const snapshot = { nodes: [...nodes], edges: [...edges] };

      onApplyChanges(result);

      const changeSummary: string[] = [];
      if (result.add_nodes?.length)
        changeSummary.push(`Added ${result.add_nodes.length} node${result.add_nodes.length > 1 ? 's' : ''}`);
      if (result.add_connections?.length)
        changeSummary.push(`Added ${result.add_connections.length} connection${result.add_connections.length > 1 ? 's' : ''}`);
      if (result.remove_nodes?.length)
        changeSummary.push(`Removed ${result.remove_nodes.length} node${result.remove_nodes.length > 1 ? 's' : ''}`);
      if (result.update_nodes?.length)
        changeSummary.push(`Updated ${result.update_nodes.length} node${result.update_nodes.length > 1 ? 's' : ''}`);

      const aiMsg: ChatMessage = {
        id: `ai_${Date.now()}`,
        role: 'assistant',
        content: result.explanation || 'Changes applied.',
        changes: result,
        snapshot,
      };
      setMessages((prev) => [...prev, aiMsg]);

      if (changeSummary.length) {
        enqueueSnackbar(changeSummary.join(', '), { variant: 'success' });
      }
    } catch (err: any) {
      const errMsg =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to process AI request';

      const aiMsg: ChatMessage = {
        id: `ai_${Date.now()}`,
        role: 'assistant',
        content: errMsg,
      };
      setMessages((prev) => [...prev, aiMsg]);
      enqueueSnackbar(errMsg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleUndo = (msg: ChatMessage) => {
    if (msg.snapshot) {
      onUndo(msg.snapshot);
      enqueueSnackbar('Reverted AI changes', { variant: 'info' });
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        bgcolor: 'background.paper',
        borderLeft: 1,
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <AiIcon fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>
            AI Workflow Editor
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {messages.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexGrow: 1,
              opacity: 0.6,
              textAlign: 'center',
              px: 3,
            }}
          >
            <AiIcon sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
            <Typography variant="body1" fontWeight={500} gutterBottom>
              Describe workflow changes in plain English
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Examples: "Add a Slack notification when API fails", "Connect the HTTP node to the email node",
              "Add error handling with an IF condition"
            </Typography>
          </Box>
        )}

        {messages.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
              {msg.role === 'assistant' ? (
                <AiIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              ) : (
                <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              )}
              <Typography variant="caption" color="text.secondary">
                {msg.role === 'user' ? 'You' : 'AI'}
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                maxWidth: '90%',
                bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {msg.content}
              </Typography>
            </Paper>

            {msg.role === 'assistant' && msg.changes && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {(msg.changes.add_nodes?.length ?? 0) > 0 && (
                  <Chip label={`+${msg.changes.add_nodes.length} node${msg.changes.add_nodes.length > 1 ? 's' : ''}`} size="small" color="success" variant="outlined" />
                )}
                {(msg.changes.add_connections?.length ?? 0) > 0 && (
                  <Chip label={`+${msg.changes.add_connections.length} conn`} size="small" color="info" variant="outlined" />
                )}
                {(msg.changes.remove_nodes?.length ?? 0) > 0 && (
                  <Chip label={`-${msg.changes.remove_nodes.length} node${msg.changes.remove_nodes.length > 1 ? 's' : ''}`} size="small" color="error" variant="outlined" />
                )}
                {(msg.changes.update_nodes?.length ?? 0) > 0 && (
                  <Chip label={`~${msg.changes.update_nodes.length} updated`} size="small" color="warning" variant="outlined" />
                )}
                {msg.snapshot && (
                  <Tooltip title="Undo these changes">
                    <IconButton size="small" onClick={() => handleUndo(msg)}>
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        ))}

        {loading && (
          <Box display="flex" alignItems="center" gap={1} sx={{ pl: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Thinking...
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      <Divider />

      {/* Input */}
      <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder="Describe what to change..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
          }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!input.trim() || loading}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' },
            borderRadius: 2,
            width: 40,
            height: 40,
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};
